import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAccountStatus } from '@/contexts/AccountStatusContext';
import { useLanguage } from '@/contexts/LanguageContext';

interface ReadOnlyWrapperProps {
  children: React.ReactNode;
  className?: string;
  tooltip?: string;
  disabled?: boolean;
}

const ReadOnlyWrapper: React.FC<ReadOnlyWrapperProps> = ({
  children,
  className = '',
  tooltip,
  disabled = false,
}) => {
  const { isReadOnly, accountStatus } = useAccountStatus();
  const { t } = useLanguage();

  const isActuallyDisabled = disabled || isReadOnly;
  
  const getTooltipContent = () => {
    if (disabled) {
      return tooltip || t('readOnly.disabled') || 'This action is disabled';
    }
    
    if (!isReadOnly || !accountStatus) {
      return tooltip;
    }

    switch (accountStatus.account_status) {
      case 'pending':
        return t('readOnly.pending') || 'Action unavailable while account is under review';
      case 'suspended':
        return t('readOnly.suspended') || 'Action unavailable while account is suspended';
      case 'rejected':
        return t('readOnly.rejected') || 'Action unavailable for rejected accounts';
      default:
        return t('readOnly.default') || 'Action unavailable in current account status';
    }
  };

  if (!isActuallyDisabled) {
    return <div className={className}>{children}</div>;
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={`${className} opacity-60 cursor-not-allowed`} style={{ pointerEvents: 'none' }}>
            {React.cloneElement(children as React.ReactElement, {
              disabled: true,
              'aria-disabled': true,
            })}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <p className="max-w-xs">{getTooltipContent()}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Higher-order component for wrapping buttons and other interactive elements
export const withReadOnlyWrapper = <P extends object>(
  Component: React.ComponentType<P>
) => {
  const WrappedComponent = (props: P & { disabled?: boolean; tooltip?: string }) => {
    const { disabled, tooltip, ...rest } = props;
    
    return (
      <ReadOnlyWrapper disabled={disabled} tooltip={tooltip}>
        <Component {...(rest as P)} disabled={disabled} />
      </ReadOnlyWrapper>
    );
  };
  
  WrappedComponent.displayName = `withReadOnlyWrapper(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

export default ReadOnlyWrapper;
